// ─── Locale configuration ───────────────────────────────────────────────────
// Single source of truth for supported locales. middleware/layout/[slug] all
// read from this list rather than hardcoding 'en'/'ar', so adding a locale
// doesn't require touching routing logic.
//
// It DOES require touching a lot else, by hand, with no single build-time
// check that catches all of it. See docs/i18n.md for the full picture —
// short version: there's a documented "System 1" (this file, dictionaries,
// LanguageSwitcher, tools.json, toolLabels — `npm run check:i18n` covers
// most of it) and an undocumented, larger "System 2" (every
// `Record<Locale, ...>`-typed object anywhere in app/ and components/,
// plus per-component `T = { en, ar }` string tables) that only `tsc
// --noEmit` / `next build` will catch. Adding a locale here breaks ~27
// files in System 2; check the doc before assuming `check:i18n` passing
// means you're done.

export const locales = ['en', 'ar', 'es', 'ru', 'fr'] as const;
export type Locale = (typeof locales)[number];

// The locale that gets NO url prefix (viptoolkit.com/jpg-to-png, not /en/jpg-to-png).
export const defaultLocale: Locale = 'en';

// Locales that render right-to-left. Drives <html dir> (see
// app/[locale]/layout.tsx, which sets dir from isRtl() below). Public-facing
// layout uses Tailwind logical-property classes (ms-/me-/ps-/pe-/start-/end-)
// rather than per-component RTL branching, so there's no single "the RTL
// pattern" file — components/tools/BackgroundRemover.tsx, despite an earlier
// comment here pointing at it, only has per-locale string tables (T.en/T.ar),
// not layout logic.
export const rtlLocales: Locale[] = ['ar'];

export function isRtl(locale: Locale): boolean {
  return rtlLocales.includes(locale);
}

export function isValidLocale(value: string): value is Locale {
  return (locales as readonly string[]).includes(value);
}

// ─── Runtime enable/disable (admin toggle) ──────────────────────────────────
// `locales` above is the STRUCTURAL list — every route, dictionary, and
// translation field that could exist. Whether a non-default locale is
// actually reachable on the live site is a separate, runtime decision an
// admin makes from Settings (SiteConfig.disabledLocales), because entering
// a full translation for every tool is a lot of work and a site may want to
// ship English-only for a while without losing a language's content/routing
// code once it's ready.
//
// Generic by design: SiteConfig.disabledLocales is a plain array of locale
// codes, and Settings (AdminSettingsForm.tsx) renders one toggle row per
// `locales.filter(l => l !== defaultLocale)` entry automatically. Adding a
// third locale to `locales` above means it shows up with a working, saved
// on/off toggle with NO further code changes here, in middleware.ts, or in
// the admin form — unlike a one-off `xEnabled` boolean per locale, which
// would need a new field, a new UI row, and a new check hand-written for
// every language added.
//
// `defaultLocale` ('en') is never toggle-able — it's the base language the
// site can't function without a URL prefix for — so it's excluded even if
// it somehow ended up in disabledLocales (e.g. a hand-edited config file).
export function getEnabledLocales(config: { disabledLocales?: Locale[] }): Locale[] {
  return locales.filter((l) => l === defaultLocale || !config.disabledLocales?.includes(l));
}

export function isLocaleEnabled(locale: Locale, config: { disabledLocales?: Locale[] }): boolean {
  return locale === defaultLocale || !config.disabledLocales?.includes(locale);
}

// Human-readable display names, keyed by locale — for admin UI labels only
// (Settings' language toggles, Tools' translation-completeness messages).
// Not used anywhere in actual site content — dictionaries and
// translations.<locale> carry their own real text per locale. Centralized
// here so adding a locale to `locales` above only requires adding a name
// here too, not hunting down every admin component with its own hardcoded
// `{ ar: 'Arabic' }`-style label map.
export const localeDisplayName: Record<Locale, string> = {
  en: 'English',
  ar: 'Arabic',
  es: 'Spanish',
  ru: 'Russian',
  fr: 'French',
};

// OpenGraph/hreflang locale codes (BCP47-ish), keyed by our short locale.
// es_ES chosen as the generic/default Spanish region (no region preference
// given when this locale was added) — revisit if the site later wants to
// target LatAm specifically (es_MX / es_419) instead.
// ru_RU is the standard/only realistic region code for Russian — no
// regional ambiguity the way es_ES vs es_MX was, so no assumption to flag.
// fr_FR chosen as the generic/default French region (no region preference
// given when this locale was added) — revisit if the site later wants to
// target Canada/Africa specifically (fr_CA, fr_419-style) instead. Same
// unresolved-assumption shape as es_ES vs es_MX above.
export const ogLocaleMap: Record<Locale, string> = {
  en: 'en_US',
  ar: 'ar_SA',
  es: 'es_ES',
  ru: 'ru_RU',
  fr: 'fr_FR',
};

// Plain BCP47 language tags — for schema.org `inLanguage` and
// `Date.toLocaleDateString()`, which want 'en-US'/'ar' rather than the
// underscore-separated ogLocaleMap codes above.
export const localeTag: Record<Locale, string> = {
  en: 'en-US',
  ar: 'ar',
  es: 'es-ES',
  ru: 'ru-RU',
  fr: 'fr-FR',
};
